import { apiClient as api } from "./httpClient";
import { ContractCustomField } from "./contractTemplatesService";

/**
 * Contratos do paciente (aba Contratos do cadastro), do usuário profissional (página aberta da lista
 * de Usuários) e o relatório Pacientes × contratos. Ver = `patient-contracts` / `professional-contracts`;
 * emitir e cancelar têm as filhas `-issue` / `-cancel`; relatório = `report-patient-contracts`.
 * O texto final é montado no servidor (dado sensível do paciente por perfil).
 */
export const CONTRACT_PERMISSIONS = {
  view: "patient-contracts",
  issue: "patient-contracts-issue",
  cancel: "patient-contracts-cancel",
  report: "report-patient-contracts",
} as const;

export const PROFESSIONAL_CONTRACT_PERMISSIONS = {
  view: "professional-contracts",
  issue: "professional-contracts-issue",
  cancel: "professional-contracts-cancel",
} as const;

/** Quem contrata: paciente ou vínculo profissional (UEPR). */
export type ContractSubject = { kind: "patient"; id: string } | { kind: "professional"; id: string };

export type ContractAudience = "patient" | "professional";

/** Indicativo das listas (RN-558e): vigente ou pendente, com o motivo para o tooltip. */
export interface ContractStanding {
  status: "vigente" | "pendente";
  detail: string;
}

const subjectPath = (s: ContractSubject) => (s.kind === "professional" ? `by-professional/${s.id}` : `by-patient/${s.id}`);

export type ContractStatus = "issued" | "signed" | "cancelled";

export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
  issued: "Emitido",
  signed: "Assinado",
  cancelled: "Cancelado",
};

export interface IssuableContractTemplate {
  templateId: string;
  title: string;
  description: string | null;
  versionId: string;
  version: number;
  requiresSignature: boolean;
  autoRenew: boolean;
  printTemplateId: string | null;
  customFields: ContractCustomField[];
}

export interface ContractRow {
  id: string;
  patientId: string | null;
  professionalRegistrationId: string | null;
  templateId: string;
  templateVersionId: string;
  templateTitle: string;
  templateVersion: number;
  /** yyyy-mm-dd */
  contractDate: string;
  validFrom: string;
  validTo: string | null;
  status: ContractStatus;
  /** HTML final, pronto para imprimir */
  content: string;
  fieldValues: Record<string, string>;
  printTemplateId: string | null;
  requiresSignature: boolean;
  /** cláusula de renovação automática: vencido o prazo segue vigente até cancelar */
  autoRenew: boolean;
  signatureStatus: string | null;
  signedAt: string | null;
  issuedByName: string | null;
  issuedByEmail: string | null;
  issuedAt: string;
  cancelledByName: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  /** "assinafy", "manual"...; nulo = nunca foi para assinatura */
  signatureProvider: string | null;
  signatureSentAt: string | null;
  hasSignedFile: boolean;
  hasSignatureEvidence: boolean;
  signers: ContractSignerRow[];
}

export interface ContractSignerRow {
  id: string;
  role: string;
  name: string;
  email: string | null;
  phone: string | null;
  channel: "email" | "whatsapp";
  order: number;
  status: "pending" | "signed" | "rejected";
  signedAt: string | null;
}

export interface ComposeContractPayload {
  versionId: string;
  date: string;
  validFrom: string;
  validTo: string | null;
  fieldValues: Record<string, string | null>;
}

export type ContractSituation = "vigente" | "vencido" | "futuro" | "sem_contrato";

export const CONTRACT_SITUATION_LABELS: Record<ContractSituation, string> = {
  vigente: "Vigente",
  vencido: "Vencido",
  futuro: "Futuro",
  sem_contrato: "Sem contrato",
};

export interface PatientContractsReportRow {
  patientId: string;
  patientName: string;
  responsibleName: string | null;
  situation: ContractSituation;
  contractId: string | null;
  templateId: string | null;
  templateTitle: string | null;
  templateVersion: number | null;
  validFrom: string | null;
  validTo: string | null;
  daysToExpire: number | null;
  requiresSignature: boolean | null;
  signatureStatus: string | null;
}

export interface PatientContractsReport {
  referenceDate: string;
  rows: PatientContractsReportRow[];
}

const ERRORS: Record<string, string> = {
  "Contracts.VersionNotPublished": "Só uma versão publicada de um modelo ativo pode ser emitida.",
  "Contracts.ValidityInvalid": "A vigência final não pode ser anterior à inicial.",
  "Contracts.DatesRequired": "Informe a data do contrato e o início da vigência.",
  "Contracts.AlreadyCancelled": "Este contrato já está cancelado.",
  "Contracts.CancelReasonRequired": "Informe o motivo do cancelamento (3 a 500 caracteres).",
  "Contracts.CancelNotAllowed": "Seu perfil não cancela contratos.",
  "Contracts.PatientNotFound": "Paciente não encontrado.",
  "Contracts.ProfessionalNotFound": "Profissional não encontrado nesta clínica.",
  "ContractTemplates.AudienceMismatch": "Este modelo não se destina a este tipo de contratante.",
};

export function contractIssueErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { code?: string; description?: string } } })?.response?.data;
  if (data?.code && ERRORS[data.code]) return ERRORS[data.code];
  // Erros de campo (obrigatório, tipo, tamanho) já vêm em português com o rótulo do campo
  if (data?.code?.startsWith("Contracts.Field") && data.description) return data.description;
  return fallback;
}

export const contractsService = {
  async getIssuableTemplates(audience: ContractAudience = "patient"): Promise<IssuableContractTemplate[]> {
    const response = await api.get<IssuableContractTemplate[]>("/contracts/templates", { params: { audience } });
    return response.data;
  },

  /** Contratos do contratante: aba do paciente ou página do profissional. */
  async getBySubject(subject: ContractSubject): Promise<ContractRow[]> {
    const response = await api.get<ContractRow[]>(`/contracts/${subjectPath(subject)}`);
    return response.data;
  },

  async getById(id: string): Promise<ContractRow> {
    const response = await api.get<ContractRow>(`/contracts/${id}`);
    return response.data;
  },

  async preview(subject: ContractSubject, payload: ComposeContractPayload): Promise<string> {
    const response = await api.post<{ content: string }>(`/contracts/${subjectPath(subject)}/preview`, payload);
    return response.data.content;
  },

  async issue(subject: ContractSubject, payload: ComposeContractPayload): Promise<string> {
    const response = await api.post<{ id: string }>(`/contracts/${subjectPath(subject)}`, payload);
    return response.data.id;
  },

  async cancel(id: string, reason: string): Promise<void> {
    await api.post(`/contracts/${id}/cancel`, { reason });
  },

  async patientsReport(templateId?: string): Promise<PatientContractsReport> {
    const response = await api.get<PatientContractsReport>("/contracts/report/patients", {
      params: templateId ? { templateId } : undefined,
    });
    return response.data;
  },
};
